import { z } from "zod";
import { defineEndpoint } from "./endpoint.js";
/**
 * Staff of a business (docs/specs/staff.md, B-71): the Owner adds people directly with a first password, which they
 * change at first sign-in; roles stay on `/v1/role-assignments`.
 */
const tag = { tag: "staff" };
const userIdParams = z.object({ userId: z.uuid() });
export const staffMemberSchema = z.object({
    userId: z.uuid(),
    name: z.string(),
    phone: z.string().nullable(),
    email: z.string().nullable(),
    status: z.enum(["active", "disabled"]),
    /** Still on the first password the business set (B-71). */
    passwordChangeRequired: z.boolean(),
    /** Belongs to this business only: the business may reset the password. */
    thisBusinessOnly: z.boolean(),
    hasPosPin: z.boolean(),
    roles: z.array(z.object({
        assignmentId: z.uuid(),
        roleId: z.uuid(),
        roleKey: z.string().nullable(),
        roleName: z.string(),
        /** null = every location. */
        locationId: z.uuid().nullable(),
    })),
    joinedAt: z.iso.datetime(),
});
export const staffListSchema = z.object({ data: z.array(staffMemberSchema) });
export const addStaffSchema = z
    .object({
    name: z.string().trim().min(1).max(100),
    phone: z.string().trim().min(10).max(32).optional(),
    email: z.email().max(254).optional(),
    /**
     * The first password of the new account. HD-01b: a phone or e-mail that already has a Sobthik account is never
     * linked here (409 `STAFF_HAS_ACCOUNT`); the business sends that person an invitation instead.
     */
    password: z.string().min(8).max(128),
    roleId: z.uuid(),
    locationId: z.uuid().nullable().default(null),
})
    .strict()
    .refine((body) => body.phone !== undefined || body.email !== undefined, {
    message: "Give a mobile number or an e-mail",
    path: ["phone"],
});
export const addStaffResultSchema = z.object({
    member: staffMemberSchema,
    /** Always false since HD-01b (an existing account is invited, not linked); kept for older clients. */
    linkedExisting: z.boolean(),
});
export const listStaff = defineEndpoint({
    ...tag,
    name: "listStaff",
    method: "GET",
    path: "/v1/staff",
    access: { permission: "staff.view" },
    summary: "Everyone in this business, with their roles",
    status: 200,
    response: staffListSchema,
});
export const addStaff = defineEndpoint({
    ...tag,
    name: "addStaff",
    method: "POST",
    path: "/v1/staff",
    access: { permission: "staff.manage" },
    summary: "Add a person with a first password and a role (they change the password at first sign-in)",
    body: addStaffSchema,
    status: 201,
    response: addStaffResultSchema,
    errors: ["STAFF_HAS_ACCOUNT"],
});
export const disableStaff = defineEndpoint({
    ...tag,
    name: "disableStaff",
    method: "POST",
    path: "/v1/staff/{userId}/disable",
    access: { permission: "staff.manage" },
    summary: "Stop a person's access to this business at once",
    params: userIdParams,
    status: 200,
    response: staffMemberSchema,
});
export const enableStaff = defineEndpoint({
    ...tag,
    name: "enableStaff",
    method: "POST",
    path: "/v1/staff/{userId}/enable",
    access: { permission: "staff.manage" },
    summary: "Give a disabled person access again",
    params: userIdParams,
    status: 200,
    response: staffMemberSchema,
});
export const resetStaffPasswordSchema = z.object({ password: z.string().min(8).max(128) }).strict();
export const resetStaffPassword = defineEndpoint({
    ...tag,
    name: "resetStaffPassword",
    method: "POST",
    path: "/v1/staff/{userId}/password",
    access: { permission: "staff.manage" },
    summary: "Set a new first password for a person of this business only (they change it at next sign-in)",
    params: userIdParams,
    body: resetStaffPasswordSchema,
    status: 204,
});
/**
 * HD-01b (docs/specs/hardening/hd-01-account-linking.md): a person who already has an account joins a business only
 * by accepting its invitation while signed in. The link goes to their own e-mail or phone; it lasts 7 days.
 */
export const staffInvitationSchema = z.object({
    id: z.uuid(),
    name: z.string(),
    phone: z.string().nullable(),
    email: z.string().nullable(),
    roleId: z.uuid(),
    roleName: z.string(),
    locationId: z.uuid().nullable(),
    expiresAt: z.iso.datetime(),
    createdAt: z.iso.datetime(),
});
export const inviteStaffSchema = z
    .object({
    phone: z.string().trim().min(10).max(32).optional(),
    email: z.email().max(254).optional(),
    roleId: z.uuid(),
    locationId: z.uuid().nullable().default(null),
})
    .strict()
    .refine((body) => body.phone !== undefined || body.email !== undefined, {
    message: "Give a mobile number or an e-mail",
    path: ["phone"],
});
export const inviteStaff = defineEndpoint({
    ...tag,
    name: "inviteStaff",
    method: "POST",
    path: "/v1/staff-invitations",
    access: { permission: "staff.manage" },
    summary: "Invite a person who already has an account; they join when they accept (a new invitation replaces theirs)",
    body: inviteStaffSchema,
    status: 201,
    response: staffInvitationSchema,
});
export const listStaffInvitations = defineEndpoint({
    ...tag,
    name: "listStaffInvitations",
    method: "GET",
    path: "/v1/staff-invitations",
    access: { permission: "staff.view" },
    summary: "Invitations waiting for an answer, newest first",
    status: 200,
    response: z.object({ data: z.array(staffInvitationSchema) }),
});
export const revokeStaffInvitation = defineEndpoint({
    ...tag,
    name: "revokeStaffInvitation",
    method: "POST",
    path: "/v1/staff-invitations/{id}/revoke",
    access: { permission: "staff.manage" },
    summary: "Withdraw an invitation that is still waiting",
    params: z.object({ id: z.uuid() }),
    status: 204,
});
/** `<tenantId>.<secret>` from the invitation link. */
const invitationTokenParams = z.object({ token: z.string().regex(/^[0-9a-f-]{36}\.[A-Za-z0-9_-]{43}$/) });
export const invitationViewSchema = z.object({
    businessName: z.string(),
    roleName: z.string(),
    invitedBy: z.string(),
    expiresAt: z.iso.datetime(),
});
export const getInvitation = defineEndpoint({
    ...tag,
    name: "getInvitation",
    method: "GET",
    path: "/v1/invitations/{token}",
    access: { signedIn: true },
    summary: "An invitation for the signed-in person (404 when it is someone else's, used, withdrawn or expired)",
    params: invitationTokenParams,
    status: 200,
    response: invitationViewSchema,
});
export const acceptInvitation = defineEndpoint({
    ...tag,
    name: "acceptInvitation",
    method: "POST",
    path: "/v1/invitations/{token}/accept",
    access: { signedIn: true },
    summary: "Join the business with the role the invitation gives",
    params: invitationTokenParams,
    status: 200,
    response: z.object({ tenantId: z.uuid(), businessName: z.string() }),
});
export const declineInvitation = defineEndpoint({
    ...tag,
    name: "declineInvitation",
    method: "POST",
    path: "/v1/invitations/{token}/decline",
    access: { signedIn: true },
    summary: "Say no to an invitation",
    params: invitationTokenParams,
    status: 204,
});
//# sourceMappingURL=staff.js.map